import React, { useRef, useState } from 'react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

const isServerSide = typeof window === 'undefined';

export const WithLazyHydration =
  (Component, opts = {}) =>
  ({ ...props }) => {
    const defaultOptions = { name: undefined, minHeight: 1000 };
    const options = { ...defaultOptions, ...opts };
    const [isHydrated, setIsHydrated] = useState(false);
    const wrapperRef = useRef(null);
    const name = props.data?.type || props.type || options.name;
    useIntersectionObserver(
      wrapperRef,
      async () => {
        // If "Component" is lazy-loadable, load it before hydrating it.
        if (Component.load) {
          await Component.load();
        }
        setIsHydrated(true);
      },
      {
        init: {
          root: undefined,
          rootMargin: '500px 0px 500px 0px',
          threshold: 0
        },
        disconnect: true,
        preflightConditionForTriggeringCallback: () => !!props?.isAboveTheFold
      }
    );

    if (isHydrated || isServerSide) {
      return <Component {...props} />;
    }

    // Only set a minimum height on local dev environment
    const styles =
      !isServerSide && window.isDev
        ? { minHeight: options.minHeight }
        : undefined;

    return (
      <div
        ref={wrapperRef}
        data-with-lazy-hydration-name={name}
        style={styles}
        // Setting InnerHTML this way leads to React attaching
        // the ref and the rest of the props to the "div" element,
        // but not deleting its children, hence skipping hydration.
        // This is the standard way in react to skip hydration and it
        // is used by several libraries.
        // oxlint-disable-next-line no-danger
        dangerouslySetInnerHTML={{ __html: '' }}
        suppressHydrationWarning
      />
    );
  };
